import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from 'src/app/core/services/auth/authentication.service';
import { LocalStorageService } from 'src/app/core/services/localStorage/localStorage.service';
import { CookieService } from 'src/app/core/services/cookie/cookie.service';

@Component({
    selector: 'app-sso-login',
    templateUrl: './sso-login.component.html',
    styleUrls: ['./sso-login.component.scss'],
})
export class SsoLoginComponent implements OnInit {
    errorMessage = '';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private authenticationService: AuthenticationService,
        private localStorageService: LocalStorageService,
        private cookieService: CookieService
    ) {}

    ngOnInit(): void {
        const token = this.route.snapshot.queryParamMap.get('token');
        const redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl');

        if (!token) {
            this.errorMessage = 'The SSO token is missing or invalid.';
            return;
        }
        const refToken = this.cookieService.getCookie('ref_token');        
        // Check if ref_token exists
        if (refToken) {
            // Call getAccessToken with ref_token
            this.authenticationService.getAccessToken(refToken).subscribe({
                next: (response: any) => {
                    if (response?.success === true) {
                        const accessToken = response?.data?.accessToken;
                        if (accessToken) {
                            this.localStorageService.setKey('access_token', accessToken);                            
                        }

                        // Add delay before navigation to ensure storage is complete
                        setTimeout(() => {
                            const targetUrl = this.getInternalRedirect(redirectUrl);
                            console.log('Navigating to target URL:', targetUrl);
                            this.router.navigateByUrl(targetUrl).then(
                                success => {
                                    if (!success) {
                                        console.error('Navigation failed, falling back to default');
                                        this.router.navigate(['/admin/university']);
                                    }
                                },
                                error => {
                                    console.error('Navigation error:', error);
                                    this.router.navigate(['/admin/university']);
                                }
                            );
                        }, 500);
                        return;
                    } else {
                        // getAccessToken failed or returned false
                        console.log('getAccessToken failed, falling back to ssoLogin');
                        this.loginWithSsoToken(token, redirectUrl);
                    }
                },
                error: (error) => {
                    console.log("getAccessToken error:", error);
                    // If getAccessToken fails, call ssoLogin directly
                    this.loginWithSsoToken(token, redirectUrl);
                },
            });
        } else {
            // No ref_token found, call ssoLogin directly
            console.log('No ref_token found, calling ssoLogin directly');
            this.loginWithSsoToken(token, redirectUrl);
        }
    }
    private loginWithSsoToken(token: string, redirectUrl: string | null): void {        
        this.authenticationService.ssoLogin(token).subscribe({
            next: (response: any) => {                
                const data = response?.data || response;
                const accessToken = data?.accessToken || data?.token;
                const userDetails = data?.user_details || data?.userDetails || data;
                const roles = data?.roles || data?.roles_permissions;

                if (!accessToken || !roles) {
                    this.errorMessage = 'The SSO response is missing login details.';
                    console.error('Missing login details:', { accessToken, roles });
                    return;
                }
                // Clear all existing data
                this.localStorageService.clearAll();                
                // Set all required data
                this.localStorageService.setKey('access_token', accessToken);
                this.localStorageService.setKey('user_details', JSON.stringify(userDetails));
                this.localStorageService.setKey('roles_permissions', JSON.stringify(roles));
                this.localStorageService.setKey('isLoggedIn', 'true');
                // CRITICAL: Add delay to ensure storage is complete and auth guard recognizes the user
                setTimeout(() => {
                    const targetUrl = this.getInternalRedirect(redirectUrl);
                    console.log('Navigating to target URL:', targetUrl);
                    
                    this.router.navigateByUrl(targetUrl).then(
                        success => {
                            console.log('Navigation success:', success);
                            if (!success) {
                                console.error('Navigation failed, falling back to default');
                                this.router.navigate(['/admin/university']);
                            }
                        },
                        error => {
                            console.error('Navigation error:', error);
                            this.router.navigate(['/admin/university']);
                        }
                    );
                }, 500);
            },
            error: (error) => {
                console.error('SSO login error:', error);
                this.errorMessage = 'SSO login failed. Please try again.';
            },
        });
    }
    private getInternalRedirect(redirectUrl: string | null): string {    
        // If no redirect URL, go to default
        if (!redirectUrl) {
            return '/admin/university';
        }
        // If it's already a relative path, use it directly
        if (redirectUrl.startsWith('/')) {
            return redirectUrl;
        }
        // Handle full URL
        if (redirectUrl.startsWith('http')) {
            try {
                const parsedUrl = new URL(redirectUrl);             
                // Return pathname + search + hash
                const targetPath = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
                return targetPath;
            } catch (error) {
                console.error('Error parsing URL:', error);
                return '/admin/university';
            }
        }

        // Fallback
        return '/admin/university';
    }

    retryLogin(): void {
        this.errorMessage = '';
        // Re-trigger the login process
        this.ngOnInit();
    }
}